import { cn } from "@/lib/utils";
import { 
  LayoutDashboard, 
  Radio,
  Settings,
  ChevronDown,
  ChevronRight,
  Sparkles,
  FileText
} from "lucide-react";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { useStations } from "@/hooks/useStations";
import { CompositeFavicon } from "@/components/ui/composite-favicon";
import { useState, useEffect } from "react";
import type { Station } from "@shared/schema";

export default function Sidebar() {
  const [location] = useLocation();
  const { user } = useAuth();
  const [expandedStations, setExpandedStations] = useState<Set<string>>(new Set());
  
  // Fetch stations using the hook that filters based on user role
  const { data: stations = [] } = useStations();
  
  // Sort stations alphabetically
  const sortedStations = [...stations].sort((a, b) => 
    a.name.localeCompare(b.name)
  );
  
  // Auto-expand active station based on current URL
  useEffect(() => {
    const match = location.match(/^\/stations\/([^\/]+)/);
    if (match) {
      const stationId = match[1];
      setExpandedStations(new Set([stationId]));
    }
  }, [location]);
  
  const toggleStationExpanded = (stationId: string) => {
    const newExpanded = new Set(expandedStations);
    if (newExpanded.has(stationId)) {
      newExpanded.delete(stationId);
    } else {
      newExpanded.add(stationId);
    }
    setExpandedStations(newExpanded);
  };
  
  const navigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  ];

  return (
    <aside className="w-80 bg-white border-r border-gray-200 min-h-screen">
      <div className="p-6">
        <nav className="space-y-2">
          {/* Dashboard Link */}
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location === item.href || location === '/dashboard';
            return (
              <Link
                key={item.name}
                href={item.href}
                className={cn(
                  "flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary-50 text-primary-700"
                    : "text-gray-700 hover:bg-gray-50"
                )}
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                <Icon className="w-5 h-5" />
                <span>{item.name}</span>
              </Link>
            );
          })}

          {/* Stations Section */}
          <div className="mt-4 space-y-1">
            {sortedStations.map((station) => {
              const isStationActive = location.startsWith(`/stations/${station.id}`);
              const isExpanded = expandedStations.has(station.id) || isStationActive;
              
              return (
                <div key={station.id}>
                  {/* Station Parent Item */}
                  <div
                    className={cn(
                      "flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer",
                      isStationActive
                        ? "bg-primary-50 text-primary-700"
                        : "text-gray-700 hover:bg-gray-50"
                    )}
                    onClick={() => toggleStationExpanded(station.id)}
                    data-testid={`nav-station-${station.id}`}
                  >
                    <div className="flex items-center space-x-3">
                      <CompositeFavicon 
                        faviconUrls={station.faviconUrls} 
                        size="sm"
                        fallbackIcon={<Radio className="w-5 h-5" />}
                      />
                      <span>{station.name}</span>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </div>
                  
                  {/* Station Sub-menu Items - Only show enabled features */}
                  {isExpanded && (
                    <div className="ml-4 mt-1 space-y-1">
                      {(station.vbrEngineEnabled ?? true) && (
                        <Link
                          href={`/stations/${station.id}/vbr`}
                          className={cn(
                            "flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors",
                            location === `/stations/${station.id}/vbr`
                              ? "bg-gray-100 text-primary-700"
                              : "text-gray-600 hover:bg-gray-50"
                          )}
                          data-testid={`nav-station-${station.id}-vbr`}
                        >
                          <Sparkles className="w-4 h-4" />
                          <span>VBR Engine</span>
                        </Link>
                      )}
                      
                      {(station.copyEngineEnabled ?? true) && (
                        <Link
                          href={`/stations/${station.id}/copy`}
                          className={cn(
                            "flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors",
                            location === `/stations/${station.id}/copy`
                              ? "bg-gray-100 text-primary-700"
                              : "text-gray-600 hover:bg-gray-50"
                          )}
                          data-testid={`nav-station-${station.id}-copy`}
                        >
                          <FileText className="w-4 h-4" />
                          <span>Copy Engine</span>
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Admin Settings Section - Only show for admin users */}
          {user?.role === 'admin' && (
            <div className="pt-6">
              <Link
                href="/admin"
                className={cn(
                  "flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                  location === "/admin"
                    ? "bg-primary-50 text-primary-700"
                    : "text-gray-700 hover:bg-gray-50"
                )}
                data-testid="nav-admin"
              >
                <Settings className="w-5 h-5" />
                <span>Admin Settings</span>
              </Link>
            </div>
          )}
        </nav>
      </div>
    </aside>
  );
}